import { Modal, Form } from "react-bootstrap";
import { Input } from "./Input";
import { Select } from "./Select";
import { Controller, useForm } from "react-hook-form";
import { Button } from "./Button";
import { HeaderText } from "./HeaderText";
import { colors } from "../Data/Common";
import { Vehicle } from "../Data/ApiTypes";
import { getStateEnumFromCode, UsState, showStateCode } from "../Data/UsState";
import { useEffect } from "react";

function sanitizeLicensePlate( value: string ): string {
    return value.replace( /[^a-zA-Z0-9]/g, "" ).toUpperCase();
}

export interface AddVehicleModalProps {
    show:     boolean;
    setShow:  ( b: boolean ) => void;
    onSubmit: ( v: Vehicle ) => void;
    isCheckout?: boolean;
}

export function AddVehicleModal( props: AddVehicleModalProps ) {
    const { show, setShow, onSubmit, isCheckout = false } = { ...props };
    const { handleSubmit, register, getValues, reset, control, formState: { errors }, setFocus }
        = useForm<Vehicle>( { 
            defaultValues: { 
                licensePlateNumber: "", 
                stateCode: "",
                make: isCheckout ? "N/A" : "",
                model: isCheckout ? "N/A" : "",
                color: isCheckout ? "N/A" : undefined
            } 
        });

    useEffect(() => {
        if (show) {
            setFocus("licensePlateNumber");
        }
    }, [show, setFocus]);

    function submit() {
        const vals = getValues();
        onSubmit( vals );
        reset( { 
            make: isCheckout ? "N/A" : "", 
            model: isCheckout ? "N/A" : "", 
            licensePlateNumber: "", 
            stateCode: "", 
            color: isCheckout ? "N/A" : undefined 
        } );
        setShow( false );
    }

    return <Modal show={props.show} centered>
            <Modal.Header>
                <HeaderText>Add Vehicle</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit( submit )}>
                    
                    <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                            <Controller
                                control={control}
                                name="licensePlateNumber"
                                rules={{ required: "License Plate Number is required" }}
                                render={( { field } ) => (
                                    <Input
                                        type="text"
                                        label="License Plate Number"
                                        formNoValidate
                                        maxLength={10}
                                        error={errors.licensePlateNumber}
                                        autoCapitalize="characters"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        {...field}
                                        value={field.value ?? ""}
                                        onChange={( e ) => {
                                            field.onChange( sanitizeLicensePlate( e.target.value ) );
                                        }}
                                    />
                                )}
                            />
                            <div style={{ marginTop: "-12px" }} className="text-muted small mb-2">
                                Enter without spaces or hyphens
                            </div>
                        </div>
                        <div style={{ width: "120px" }}>
                            <Select
                                label="State"
                                {...register( "stateCode", {
                                    required: "State is required",
                                    validate: ( value ) => {
                                        if( !value )
                                            return true;
                                        const stateEnum = getStateEnumFromCode( value );
                                        return stateEnum !== undefined || "Invalid state code";
                                    },
                                } )}
                                error={errors.stateCode}>
                                <option value="">Select</option>
                                {Object.values(UsState)
                                    .filter(value => typeof value === 'number')
                                    .map(stateEnum => (
                                        <option key={stateEnum} value={showStateCode(stateEnum as UsState)}>
                                            {showStateCode(stateEnum as UsState)}
                                        </option>
                                    ))}
                            </Select>
                        </div>
                    </div>
                    {!isCheckout && (
                        <>
                            <div className="d-flex flex-row w-100 gap-3 mb-3">
                                <Input className="w-100"
                                    type="text"
                                    label="Make"
                                    formNoValidate
                                    {...register( "make", { required: "Make is required" } )}
                                    error={errors.make} />
                                <Input className="w-100"
                                    type="text"
                                    label="Model"
                                    formNoValidate
                                    {...register( "model", { required: "Model is required" } )}
                                    error={errors.model} />
                            </div>
                           
                            <div className="mb-3">
                                Vehicle Color
                                <Controller
                                    control={control}
                                    name="color"
                                    rules={{ required: "Vehicle Color is required" }}
                                    render={( { field: { onChange, onBlur, value, ref } } ) => {
                                        return <div className="mb-2" style={{  width: "100%",
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(6, 1fr)',
                                            justifyItems: "center",
                                            gap: '0.20em' }}>
                                            {colors.map( col => {
                                                const sel = value === col ? "border border-primary-subtle border-4 shadow" : "";
                                                return <div key={col} className={`${sel} m-1 border d-inline-block`}
                                                    style={{ height: 48, width: 48, backgroundColor: col }}
                                                    onClick={ () => onChange( col ) }>
                                                </div>;
                                            } )}
                                        </div>;
                                    } } />
                            </div>
                            <div className="mb-3">
                                {errors.color && <Form.Text className="d-block text-danger">
                                    {errors.color.message}
                                </Form.Text>}
                            </div>
                        </>
                    )}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button type="button" className="w-100" onClick={() => {setShow( false ); reset(); }}>
                            Cancel
                        </Button>
                        <Button type="submit" className="w-100 btn-main">
                            Add Vehicle
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
}